<template>
  <BaseDialog
    :title="$t('survey.editTitleAndMessage')"
    :is-open="isOpen"
    type="responsive"
    :align-dismiss-button-right="true"
    @close="closeDialog"
  >
    <BaseInputField
      v-model="formData.title"
      type="text"
      :label="$t('common.title')"
      class="bg-surface-container-high"
    />
    <DialogBreak />
    <WysiwygTextField v-model="formData.message" :placeholder="$t('survey.messagePlaceholder')" />

    <template #confirm>
      <BaseTextButton color="filled" :disabled="isSaving || !isFormValid" @click="saveAssessmentEdits">
        {{ $t('common.save') }}
      </BaseTextButton>
    </template>
    <template #dismiss>
      <BaseTextButton color="text" :disabled="isSaving" @click="closeDialog">
        {{ $t('common.cancel') }}
      </BaseTextButton>
    </template>
  </BaseDialog>
</template>

<script setup lang="ts">
  import { computed, watch } from 'vue'
  import BaseDialog from '@/core/components/base/dialog/BaseDialog.vue'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import BaseInputField from '@/core/components/base/form-fields/BaseInputField.vue'
  import DialogBreak from '@/core/components/base/dialog/DialogBreak.vue'
  import WysiwygTextField from '@/core/components/base/wysiwyg/WysiwygTextField.vue'
  import type { AssessmentDetail } from './use-assessment'
  import { useEditAssessment } from './use-edit-assessment'

  interface Props {
    isOpen: boolean
    assessment: AssessmentDetail
  }

  const props = defineProps<Props>()

  const emit = defineEmits<{
    (e: 'close-dialog'): void
    (e: 'assessment-updated'): void
  }>()

  const closeDialog = () => {
    emit('close-dialog')
    resetForm()
  }

  watch(
    () => props.isOpen,
    () => {
      resetForm()
    },
  )

  const { isFormValid, saveAssessmentEdits, resetForm, formData, isSaving } = useEditAssessment(
    computed(() => props.assessment),
    () => {
      emit('assessment-updated')
      closeDialog()
    },
  )
</script>
